import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import authHeader from "../services/auth-header";
import URL from "../common/GetUrl";
import DataTable from "react-data-table-component";
import ArrowDownward from "@material-ui/icons/ArrowDownward";
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Avatar,
  Stack,
  Fade,
  Divider,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Collapse,
  IconButton,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import TuneIcon from "@mui/icons-material/Tune";
import TableChartIcon from "@mui/icons-material/TableChart";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ScienceIcon from "@mui/icons-material/Science";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const API_URL = URL();
const sortIcon = <ArrowDownward />;

const GREEN = {
  900: "#1b5e20",
  800: "#2e7d32",
  700: "#388e3c",
  600: "#43a047",
  500: "#4caf50",
  400: "#66bb6a",
  100: "#c8e6c9",
  50: "#e8f5e9",
};

const truncateStyle = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "block",
  maxWidth: "100%",
};

const TruncatedCell = ({ value, maxWidth = "100%" }) => {
  const text = value || "—";
  const isEmpty = !value || value === "";
  return (
    <Tooltip title={isEmpty ? "" : text} arrow placement="top-start" enterDelay={400}>
      <span style={{ ...truncateStyle, maxWidth, color: isEmpty ? "#bdbdbd" : "inherit" }}>
        {text}
      </span>
    </Tooltip>
  );
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: "flex", py: 0.4, gap: 1 }}>
    <Typography variant="caption" sx={{ fontWeight: 700, color: "#546e7a", minWidth: 140, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ color: "#263238", wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const ExpandedRowDetail = ({ data }) => (
  <Box
    sx={{
      px: 3,
      py: 2,
      bgcolor: "#f8faf8",
      borderBottom: `2px solid ${GREEN[100]}`,
    }}
  >
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: GREEN[800], textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5, display: "block" }}>
          Plant Info
        </Typography>
        <DetailRow label="Plant ID" value={data.idPlant} />
        <DetailRow label="Plant Index" value={data.PlantIndex} />
        <DetailRow label="External ID" value={data.externalID} />
        <DetailRow label="Planting Date" value={data.PlantingDate} />
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: GREEN[800], textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5, display: "block" }}>
          Phenotype Measurement
        </Typography>
        <DetailRow label="Phenotype" value={data.PhenotypeName} />
        <DetailRow label="Description" value={data.PhenotypeDescription} />
        <DetailRow label="Type" value={data.MeasurementType} />
        <DetailRow label="Value" value={data.MeasurementValue} />
        <DetailRow label="Timestamp" value={data.MeasurementTimestamp} />
        <DetailRow label="Year" value={data.MeasurementYear} />
        <DetailRow label="Notes" value={data.MeasurementNotes} />
        <DetailRow label="Data File" value={data.DataFile} />
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: GREEN[800], textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5, display: "block" }}>
          Location
        </Typography>
        <DetailRow label="Location ID" value={data.idLocation} />
        <DetailRow label="Row / Column" value={data.locationRow && data.locationColumn ? `${data.locationRow} / ${data.locationColumn}` : null} />
        <DetailRow label="GPS" value={data.locationGPS} />
        <DetailRow label="Rootstock" value={data.rootstock} />
        <DetailRow label="Start Date" value={data.locationStartDate} />
        <DetailRow label="End Date" value={data.locationEndDate} />
      </Grid>
    </Grid>
  </Box>
);

const TABLE_COLUMNS = [
  { name: "Year", selector: (r) => r.MeasurementYear, sortable: true, width: "72px", compact: true, center: true,
    cell: (r) => <span style={{ fontWeight: 600, color: GREEN[800] }}>{r.MeasurementYear || "—"}</span> },
  { name: "Plant ID", selector: (r) => r.idPlant, sortable: true, width: "90px", compact: true },
  { name: "Plant Index", selector: (r) => r.PlantIndex, sortable: true, width: "100px", compact: true },
  { name: "External ID", selector: (r) => r.externalID, sortable: true, width: "105px", compact: true,
    cell: (r) => <TruncatedCell value={r.externalID} /> },
  { name: "Phenotype", selector: (r) => r.PhenotypeName, sortable: true, width: "170px",
    cell: (r) => <TruncatedCell value={r.PhenotypeName} /> },
  { name: "Value", selector: (r) => r.MeasurementValue, sortable: true, width: "90px", compact: true,
    cell: (r) => <span style={{ fontWeight: 600 }}>{r.MeasurementValue || "—"}</span> },
  { name: "Type", selector: (r) => r.MeasurementType, sortable: true, width: "100px", compact: true,
    cell: (r) => <TruncatedCell value={r.MeasurementType} /> },
  { name: "Description", selector: (r) => r.PhenotypeDescription, sortable: false, width: "180px",
    cell: (r) => <TruncatedCell value={r.PhenotypeDescription} /> },
  { name: "Row", selector: (r) => r.locationRow, sortable: true, width: "64px", compact: true, center: true },
  { name: "Col", selector: (r) => r.locationColumn, sortable: true, width: "64px", compact: true, center: true },
  { name: "Rootstock", selector: (r) => r.rootstock, sortable: true, width: "100px", compact: true,
    cell: (r) => <TruncatedCell value={r.rootstock} /> },
  { name: "Planting Date", selector: (r) => r.PlantingDate, sortable: true, width: "110px", compact: true },
  { name: "Timestamp", selector: (r) => r.MeasurementTimestamp, sortable: true, width: "155px", compact: true,
    cell: (r) => <TruncatedCell value={r.MeasurementTimestamp} /> },
  { name: "Notes", selector: (r) => r.MeasurementNotes, sortable: false, width: "160px",
    cell: (r) => <TruncatedCell value={r.MeasurementNotes} /> },
  { name: "Data File", selector: (r) => r.DataFile, sortable: false, width: "120px",
    cell: (r) => <TruncatedCell value={r.DataFile} /> },
  { name: "GPS", selector: (r) => r.locationGPS, sortable: false, width: "120px",
    cell: (r) => <TruncatedCell value={r.locationGPS} /> },
  { name: "Loc. Start", selector: (r) => r.locationStartDate, sortable: true, width: "105px", compact: true },
  { name: "Loc. End", selector: (r) => r.locationEndDate, sortable: true, width: "105px", compact: true },
  { name: "Phenotype ID", selector: (r) => r.idPhenotype, sortable: true, width: "100px", compact: true, center: true },
  { name: "Location ID", selector: (r) => r.idLocation, sortable: true, width: "100px", compact: true, center: true },
  { name: "Measure ID", selector: (r) => r.idPhenotypeMeasurement, sortable: true, width: "100px", compact: true, center: true },
];

const dataTableStyles = {
  headRow: {
    style: {
      backgroundColor: GREEN[50],
      borderBottom: `2px solid ${GREEN[400]}`,
      minHeight: "40px",
    },
  },
  headCells: {
    style: {
      paddingLeft: "10px",
      paddingRight: "10px",
      fontWeight: 700,
      fontSize: "12px",
      color: GREEN[900],
      textTransform: "uppercase",
      letterSpacing: "0.03em",
    },
  },
  rows: {
    style: {
      fontSize: "13px",
      color: "#37474f",
      minHeight: "42px",
      borderBottom: "1px solid #f5f5f5",
      "&:nth-of-type(even)": { backgroundColor: "#fafcfa" },
    },
    highlightOnHoverStyle: {
      backgroundColor: GREEN[50],
      borderBottomColor: GREEN[100],
      outline: "none",
    },
  },
  cells: {
    style: {
      paddingLeft: "10px",
      paddingRight: "10px",
    },
  },
  pagination: {
    style: {
      borderTop: `1px solid ${GREEN[100]}`,
      fontSize: "13px",
      color: "#546e7a",
      minHeight: "48px",
    },
  },
};

const PhenotypeReports = () => {
  const searchCardRef = React.useRef(null);
  const [searchMode, setSearchMode] = useState("plant");
  const [plantIdType, setPlantIdType] = useState("verinomics");
  const [plantId, setPlantId] = useState("");
  const [row, setRow] = useState("");
  const [col, setCol] = useState("");
  const [phenotypeOptions, setPhenotypeOptions] = useState([]);
  const [selectedPhenotypes, setSelectedPhenotypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [filterText, setFilterText] = useState("");

  useEffect(() => {
    axios
      .get(API_URL + "phenotypes", { headers: authHeader() })
      .then((res) => {
        const opts = res.data.result || [];
        opts.sort((a, b) => a.PhenotypeName.localeCompare(b.PhenotypeName));
        setPhenotypeOptions(opts);
      })
      .catch(() => setPhenotypeOptions([]));
  }, []);

  const canSubmit =
    !loading &&
    (searchMode === "plant"
      ? plantId.trim() !== ""
      : row.trim() !== "" && col.trim() !== "");

  const handleSearchModeChange = (_, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
      setError(null);
    }
  };

  const handlePlantIdTypeChange = (_, newType) => {
    if (newType !== null) {
      setPlantIdType(newType);
      setPlantId("");
      setError(null);
    }
  };

  const handleRunQuery = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResults(null);
    setFilterText("");

    const phenotypes = selectedPhenotypes.map((p) => p.idPhenotype);
    const payload = { searchMode };

    if (searchMode === "plant") {
      payload.plantIdType = plantIdType;
      if (plantIdType === "burchell") {
        payload.burchellId = plantId.trim();
      } else {
        payload.plantId = parseInt(plantId, 10);
      }
    } else {
      payload.row = parseInt(row, 10);
      payload.col = parseInt(col, 10);
    }

    if (phenotypes.length > 0) {
      payload.phenotypeIds = phenotypes;
    }

    try {
      const response = await axios.post(API_URL + "phenotype-report", payload, {
        headers: authHeader(),
      });
      setResults(response.data);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.message ||
        "An unexpected error occurred.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [searchMode, plantIdType, plantId, row, col, selectedPhenotypes]);

  const handleDownload = useCallback(() => {
    if (!results?.csv) return;
    const blob = new Blob([results.csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", results.filename || "phenotype_report.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, [results]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && canSubmit) handleRunQuery();
  };

  const handleClearSearch = useCallback(() => {
    setPlantId("");
    setRow("");
    setCol("");
    setSelectedPhenotypes([]);
    setResults(null);
    setFilterText("");
    setError(null);
    setTimeout(() => {
      searchCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, []);

  const filteredRows = results?.rows
    ? results.rows.filter((r) => {
        if (!filterText) return true;
        const lower = filterText.toLowerCase();
        return Object.values(r).some((v) =>
          String(v).toLowerCase().includes(lower)
        );
      })
    : [];

  return (
    <Container
      maxWidth="xl"
      disableGutters
      sx={{
        minHeight: "calc(100vh - 100px)",
        py: { xs: 3, md: 5 },
        px: { xs: 2, md: 3 },
      }}
    >
      <Fade in timeout={600}>
        <Paper
          elevation={0}
          sx={{
            px: { xs: 3, md: 6 },
            py: { xs: 4, md: 6 },
            mb: 4,
            borderRadius: 4,
            background: "#fff",
            border: "1px solid",
            borderColor: "grey.100",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Accent bar */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: 120,
              height: 4,
              borderRadius: "0 0 4px 4px",
              background: `linear-gradient(90deg, ${GREEN[500]}, ${GREEN[400]})`,
            }}
          />

          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Avatar sx={{ width: 52, height: 52, bgcolor: GREEN[50] }}>
              <AssessmentIcon sx={{ fontSize: 28, color: GREEN[700] }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 800,
                  color: "#1a2e1a",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                }}
              >
                Phenotype Reports
              </Typography>
              <Typography variant="body2" sx={{ color: "#78909c", mt: 0.3 }}>
                Query the phenotype database and download CSV exports.
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* Search Parameters Card */}
          <Card
            ref={searchCardRef}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "grey.200",
              overflow: "visible",
              mb: 4,
              scrollMarginTop: "24px",
            }}
          >
            {/* Card Header */}
            <Box
              sx={{
                px: 3,
                py: 2,
                bgcolor: GREEN[50],
                borderBottom: "1px solid",
                borderColor: GREEN[100],
                display: "flex",
                alignItems: "center",
              }}
            >
              <FilterListIcon sx={{ fontSize: 20, mr: 1, color: GREEN[700] }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: GREEN[900] }}>
                Search Parameters
              </Typography>
            </Box>

            <Box sx={{ p: 3 }}>
              {/* Search Mode Toggle */}
              <Typography
                variant="body2"
                sx={{ color: "#546e7a", mb: 1.5, fontWeight: 600, fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.04em" }}
              >
                Search Method
              </Typography>
              <ToggleButtonGroup
                value={searchMode}
                exclusive
                onChange={handleSearchModeChange}
                size="small"
                sx={{
                  mb: 3,
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    py: 0.9,
                    borderColor: GREEN[400],
                    color: GREEN[800],
                    "&.Mui-selected": {
                      bgcolor: GREEN[600],
                      color: "#fff",
                      borderColor: GREEN[600],
                      "&:hover": { bgcolor: GREEN[700] },
                    },
                    "&:hover": { bgcolor: GREEN[50] },
                  },
                }}
              >
                <ToggleButton value="plant">
                  <LocalFloristIcon sx={{ mr: 1, fontSize: 18 }} />
                  Search by Plant
                </ToggleButton>
                <ToggleButton value="location">
                  <LocationOnIcon sx={{ mr: 1, fontSize: 18 }} />
                  Search by Location
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Plant ID Type Sub-Toggle */}
              {searchMode === "plant" && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#546e7a", mb: 1, fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em" }}
                  >
                    ID Type
                  </Typography>
                  <ToggleButtonGroup
                    value={plantIdType}
                    exclusive
                    onChange={handlePlantIdTypeChange}
                    size="small"
                    sx={{
                      "& .MuiToggleButton-root": {
                        textTransform: "none",
                        fontWeight: 600,
                        px: 2.5,
                        py: 0.7,
                        fontSize: "13px",
                        borderColor: GREEN[400],
                        color: GREEN[800],
                        "&.Mui-selected": {
                          bgcolor: GREEN[600],
                          color: "#fff",
                          borderColor: GREEN[600],
                          "&:hover": { bgcolor: GREEN[700] },
                        },
                        "&:hover": { bgcolor: GREEN[50] },
                      },
                    }}
                  >
                    <ToggleButton value="verinomics">
                      Verinomics ID (idPlant)
                    </ToggleButton>
                    <ToggleButton value="burchell">
                      Burchell ID (PlantCodes)
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}

              {/* Input Fields — single consistent Grid for all modes */}
              <Grid container spacing={2.5} alignItems="flex-start">
                {searchMode === "plant" && (
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label={plantIdType === "burchell" ? "Burchell ID (PlantCodes)" : "Verinomics ID (idPlant)"}
                      placeholder={plantIdType === "burchell" ? "e.g. BBx0P152A1" : "e.g. 12241"}
                      value={plantId}
                      onChange={(e) => setPlantId(e.target.value)}
                      onKeyDown={handleKeyDown}
                      size="small"
                      type={plantIdType === "burchell" ? "text" : "number"}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocalFloristIcon sx={{ fontSize: 18, color: GREEN[500] }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )}

                {searchMode === "location" && (
                  <>
                    <Grid item xs={12} sm={3} md={2}>
                      <TextField
                        fullWidth
                        label="Row"
                        placeholder="e.g. 5"
                        value={row}
                        onChange={(e) => setRow(e.target.value)}
                        onKeyDown={handleKeyDown}
                        size="small"
                        type="number"
                        required
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOnIcon sx={{ fontSize: 18, color: GREEN[500] }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} md={2}>
                      <TextField
                        fullWidth
                        label="Column"
                        placeholder="e.g. 12"
                        value={col}
                        onChange={(e) => setCol(e.target.value)}
                        onKeyDown={handleKeyDown}
                        size="small"
                        type="number"
                        required
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOnIcon sx={{ fontSize: 18, color: GREEN[500] }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={6} md={5}>
                  <Autocomplete
                    multiple
                    options={phenotypeOptions}
                    getOptionLabel={(option) => option.PhenotypeName}
                    isOptionEqualToValue={(option, value) => option.idPhenotype === value.idPhenotype}
                    value={selectedPhenotypes}
                    onChange={(_, newValue) => setSelectedPhenotypes(newValue)}
                    size="small"
                    disableCloseOnSelect
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.idPhenotype}
                          label={option.PhenotypeName}
                          size="small"
                          sx={{
                            bgcolor: GREEN[50],
                            color: GREEN[900],
                            fontWeight: 600,
                            fontSize: "12px",
                            borderColor: GREEN[400],
                            "& .MuiChip-deleteIcon": { color: GREEN[600], "&:hover": { color: GREEN[900] } },
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Phenotypes (optional)"
                        placeholder={selectedPhenotypes.length === 0 ? "Select phenotypes..." : ""}
                        variant="outlined"
                        helperText="Leave blank for all phenotypes."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <ScienceIcon sx={{ fontSize: 18, color: GREEN[500] }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                      },
                    }}
                  />
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} sx={{ mt: 3, pt: 2.5, borderTop: "1px solid", borderColor: "grey.100" }}>
                <Button
                  variant="contained"
                  startIcon={
                    loading ? (
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                    ) : (
                      <TuneIcon />
                    )
                  }
                  disableElevation
                  disabled={!canSubmit}
                  onClick={handleRunQuery}
                  sx={{
                    bgcolor: GREEN[600],
                    color: "#fff",
                    borderRadius: 2,
                    px: 3.5,
                    py: 1,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    "&:hover": { bgcolor: GREEN[800] },
                    "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#9e9e9e" },
                  }}
                >
                  {loading ? "Running Query..." : "Run Query"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  disabled={!results?.csv}
                  onClick={handleDownload}
                  sx={{
                    color: GREEN[800],
                    borderColor: GREEN[400],
                    borderRadius: 2,
                    px: 3.5,
                    py: 1,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    "&:hover": {
                      borderColor: GREEN[700],
                      bgcolor: GREEN[50],
                    },
                  }}
                >
                  Download CSV
                </Button>
                {results && (
                  <Fade in timeout={300}>
                    <Button
                      variant="outlined"
                      startIcon={<RestartAltIcon />}
                      onClick={handleClearSearch}
                      sx={{
                        color: "#546e7a",
                        borderColor: "#cfd8dc",
                        borderRadius: 2,
                        px: 3.5,
                        py: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "14px",
                        "&:hover": {
                          borderColor: "#90a4ae",
                          bgcolor: "#eceff1",
                          color: "#37474f",
                        },
                      }}
                    >
                      Clear & New Search
                    </Button>
                  </Fade>
                )}
              </Stack>
            </Box>
          </Card>

          {/* Error Alert */}
          <Collapse in={!!error}>
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2 }}
              action={
                <IconButton size="small" onClick={() => setError(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          </Collapse>

          <Divider sx={{ my: 3 }} />

          {/* Results Section */}
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#1a2e1a", mb: 2 }}
          >
            <TableChartIcon
              sx={{
                fontSize: 20,
                mr: 1,
                verticalAlign: "text-bottom",
                color: GREEN[600],
              }}
            />
            Results
            {results && (
              <Chip
                label={`${results.rowCount} row${results.rowCount !== 1 ? "s" : ""}`}
                size="small"
                sx={{
                  ml: 1.5,
                  bgcolor: GREEN[50],
                  color: GREEN[800],
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              />
            )}
          </Typography>

          {/* Results Table or Empty State */}
          {results && results.rows.length > 0 ? (
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "grey.200",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: "#fafbfa",
                  borderBottom: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Download CSV">
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleDownload}
                        sx={{ color: GREEN[600] }}
                      >
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Typography variant="body2" sx={{ color: "#78909c", fontWeight: 500 }}>
                    {results.filename}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <TextField
                    size="small"
                    placeholder="Filter results..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: "#90a4ae" }} />
                        </InputAdornment>
                      ),
                      endAdornment: filterText ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setFilterText("")}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={{
                      width: 260,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        fontSize: "13px",
                      },
                    }}
                  />
                  <Tooltip title="Clear results and start a new search">
                    <Button
                      size="small"
                      startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
                      onClick={handleClearSearch}
                      sx={{
                        color: "#78909c",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "12px",
                        borderRadius: 2,
                        px: 1.5,
                        whiteSpace: "nowrap",
                        "&:hover": {
                          bgcolor: "#eceff1",
                          color: "#37474f",
                        },
                      }}
                    >
                      New Search
                    </Button>
                  </Tooltip>
                </Stack>
              </Box>
              <DataTable
                columns={TABLE_COLUMNS}
                data={filteredRows}
                sortIcon={sortIcon}
                pagination
                paginationPerPage={25}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                customStyles={dataTableStyles}
                highlightOnHover
                responsive
                fixedHeader
                fixedHeaderScrollHeight="520px"
                expandableRows
                expandableRowsComponent={ExpandedRowDetail}
                expandableRowsHideExpander={false}
                defaultSortFieldId={1}
              />
            </Card>
          ) : results && results.rows.length === 0 ? (
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px dashed",
                borderColor: "#ffcc80",
                bgcolor: "#fff8e1",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                }}
              >
                <Avatar sx={{ width: 56, height: 56, bgcolor: "#fff3e0", mb: 2 }}>
                  <SearchIcon sx={{ fontSize: 28, color: "#ff9800" }} />
                </Avatar>
                <Typography
                  variant="body1"
                  sx={{ color: "#e65100", fontWeight: 600, mb: 0.5 }}
                >
                  No results found
                </Typography>
                <Typography variant="body2" sx={{ color: "#bf360c", mb: 2 }}>
                  No phenotype data matched your search criteria. Try different parameters.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltIcon />}
                  onClick={handleClearSearch}
                  sx={{
                    color: "#e65100",
                    borderColor: "#ffcc80",
                    borderRadius: 2,
                    px: 3,
                    py: 0.8,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "13px",
                    "&:hover": {
                      borderColor: "#ff9800",
                      bgcolor: "#fff3e0",
                    },
                  }}
                >
                  Clear & Try Again
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px dashed",
                borderColor: "grey.300",
                bgcolor: "#fafbfa",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 8,
                }}
              >
                <Avatar sx={{ width: 64, height: 64, bgcolor: GREEN[50], mb: 2 }}>
                  <AssessmentIcon sx={{ fontSize: 32, color: GREEN[400] }} />
                </Avatar>
                <Typography
                  variant="body1"
                  sx={{ color: "#90a4ae", fontWeight: 600, mb: 0.5 }}
                >
                  No results yet
                </Typography>
                <Typography variant="body2" sx={{ color: "#b0bec5" }}>
                  Set your parameters above and run a query to see phenotype data here.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Chip
                    label="CSV Export"
                    size="small"
                    sx={{ bgcolor: GREEN[50], color: GREEN[800] }}
                  />
                  <Chip
                    label="Sortable"
                    size="small"
                    sx={{ bgcolor: GREEN[50], color: GREEN[800] }}
                  />
                  <Chip
                    label="Filterable"
                    size="small"
                    sx={{ bgcolor: GREEN[50], color: GREEN[800] }}
                  />
                </Stack>
              </CardContent>
            </Card>
          )}
        </Paper>
      </Fade>
    </Container>
  );
};

export default PhenotypeReports;
